import { useEffect, useId, useRef, type ReactNode, type RefObject } from "react";
import { cn } from "../lib/cn.js";

/**
 * Phase C.2 共用 Drawer — 侧滑面板，用于审计 row 详情 / RolesPanel dosia 编辑等
 * 内容偏大场景。dosia-bg-light 表面 + dosia-border。
 *
 * Optional a11y extensions (backward compatible):
 * - titleId / aria-labelledby for dialog name
 * - focus close (or initialFocusRef) on open
 * - restore focus to returnFocusRef on close
 */
export function Drawer({
  open,
  onClose,
  title,
  footer,
  children,
  side = "right",
  width = 480,
  closeLabel = "关闭",
  titleId,
  initialFocusRef,
  returnFocusRef,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  side?: "right" | "left";
  width?: number;
  /** Accessible close control label; pass i18n so non-zh locales stay consistent. */
  closeLabel?: string;
  /** Stable id for the title element; enables dialog aria-labelledby. */
  titleId?: string;
  /** Optional override for the element focused when the drawer opens. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Element that opened the drawer; receives focus when it closes. */
  returnFocusRef?: RefObject<HTMLElement | null>;
}): JSX.Element | null {
  const autoTitleId = useId();
  const resolvedTitleId = titleId ?? (title != null ? autoTitleId : undefined);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const focusable = [...(dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ) ?? [])].filter((element) => !element.hasAttribute("hidden"));
      if (focusable.length === 0) {
        e.preventDefault();
        dialogRef.current?.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && (document.activeElement === first || !dialogRef.current?.contains(document.activeElement))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !dialogRef.current?.contains(document.activeElement))) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const focusTarget = initialFocusRef?.current ?? closeBtnRef.current;
    const frame = window.requestAnimationFrame(() => {
      focusTarget?.focus();
    });
    return () => {
      window.cancelAnimationFrame(frame);
      const restore = returnFocusRef?.current;
      if (restore && typeof restore.focus === "function") {
        restore.focus();
      }
    };
  }, [open, initialFocusRef, returnFocusRef]);

  if (!open) return null;

  return (
    <div
      role="presentation"
      className="fixed inset-0 z-40 bg-dosia-bg/60"
      onClick={onClose}
    >
      <aside
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby={resolvedTitleId}
        style={{ width: `min(${width}px, 100vw)` }}
        className={cn(
          "absolute top-0 bottom-0 bg-dosia-bg-light border-dosia-border shadow-2xl flex flex-col",
          side === "right" ? "right-0 border-l" : "left-0 border-r",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {title ? (
          <div className="px-4 py-3 border-b border-dosia-border flex items-center justify-between">
            <div
              id={resolvedTitleId}
              className="text-sm font-semibold text-dosia-text"
            >
              {title}
            </div>
            <button
              ref={closeBtnRef}
              type="button"
              onClick={onClose}
              className="text-dosia-text-secondary hover:text-dosia-text text-base leading-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dosia-accent"
              aria-label={closeLabel}
            >
              ×
            </button>
          </div>
        ) : null}
        <div className="flex-1 overflow-auto px-4 py-3">{children}</div>
        {footer ? (
          <div className="px-4 py-3 border-t border-dosia-border flex justify-end gap-2">
            {footer}
          </div>
        ) : null}
      </aside>
    </div>
  );
}
